import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { resolve } from 'node:path';
import fs from 'node:fs';
import crypto from 'node:crypto';
import { cachified } from '@epic-web/cachified';
import { CacheKey, lru } from '~/shell/cache.server';
import { useSubmit } from 'react-router';
import { getLogger } from '~/logging/logger.ts';
import { getOptionalLoginInfo } from '~/shell/globalContext.ts';

const log = getLogger('tgs-version-check');
const clientLog = getLogger('version-check');
const componentLog = getLogger('version-check-component');

const VERSION_CHECK_INTERVAL = 60_000;

async function generateBuildHashFromFilenames() {
  try {
    const buildDir = resolve(process.cwd(), 'build', 'client', 'assets');

    const files = await fs.promises.readdir(buildDir);
    const jsFiles = files.filter(file => file.endsWith('.js')).sort((a, b) => a.localeCompare(b));

    const combinedFilenames = jsFiles.join('');

    const hash = crypto.createHash('sha256').update(combinedFilenames).digest('hex');

    return hash;
  } catch {
    return 'fakeHash';
  }
}

export const loader = async () => {
  let versionHash = 'fakeHash';
  const loginInfo = getOptionalLoginInfo();

  try {
    if (process.env.NODE_ENV === 'production') {
      versionHash = await cachified({
        key: CacheKey.Version,
        cache: lru,
        async getFreshValue() {
          return generateBuildHashFromFilenames();
        }
      });
    } else {
      // No caching in development mode
      versionHash = await generateBuildHashFromFilenames();
    }
  } catch {
    log.error('Fetching version hash failed');
  }

  return Response.json({
    versionHash: versionHash,
    isAuthenticated: !!loginInfo
  });
};

export function useVersionCheck() {
  const [initialVersionHash, setInitialVersionHash] = useState<string | null>(null);
  const [versionHash, setVersionHash] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);
  const intervalIdRef = useRef<number | null>(null);
  const isWindowFocusedRef = useRef<boolean>(typeof document !== 'undefined' ? document.hasFocus() : false);
  const initialVersionHashRef = useRef<string | null>(null);
  const submit = useSubmit();
  const sessionExpiredHandledRef = useRef(false);
  const hasChanged = useMemo(() => {
    return initialVersionHash && versionHash && initialVersionHash !== versionHash;
  }, [initialVersionHash, versionHash]);

  const checkVersion = useCallback(async () => {
    if (!isWindowFocusedRef.current) {
      clientLog.debug('Version check skipped, window not focused');
      return;
    }

    try {
      const response = await fetch('/infra/versioninfo');
      if (response.ok) {
        const result = await response.json();

        if (!initialVersionHashRef.current && result?.versionHash) {
          initialVersionHashRef.current = result.versionHash;
          setInitialVersionHash(result.versionHash);
        }

        if (result?.versionHash) {
          setVersionHash(result.versionHash);
        }

        setIsAuthenticated(result?.isAuthenticated);
      } else if (response.status === 401) {
        // Server middleware signalled a broken session. Auth cookies have already been
        // cleared in the response. Instead of a hard reload (which would show the login
        // button), trigger a silent Keycloak SSO re-auth with prompt=none: if the SSO
        // session is still alive, the user gets new tokens + loginInfo and continues
        // seamlessly; otherwise they end up on the login page — same end state as a
        // reload, but without the interim anonymous UI flicker.
        if (sessionExpiredHandledRef.current) return;
        sessionExpiredHandledRef.current = true;

        const formData = new FormData();
        formData.append('redirectUrl', location.pathname + location.search);
        formData.append('silent', 'true');
        submit(formData, { method: 'post', action: '/infra/auth/login', navigate: false });
      }
    } catch {
      clientLog.error('Error checking version');
    }
  }, [submit]);

  useEffect(() => {
    isWindowFocusedRef.current = document.hasFocus();

    if (isWindowFocusedRef.current) {
      checkVersion();
    }

    const handleWindowFocus = () => {
      isWindowFocusedRef.current = true;
      checkVersion();
    };

    const handleWindowBlur = () => {
      isWindowFocusedRef.current = false;
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    intervalIdRef.current = window.setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => {
      if (intervalIdRef.current !== null) {
        window.clearInterval(intervalIdRef.current);
      }
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [checkVersion]);

  useEffect(() => {
    if (hasChanged && intervalIdRef.current !== null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, [hasChanged]);

  return {
    hasChanged,
    initialVersionHash,
    versionHash,
    isAuthenticated
  };
}

/**
 * Checks if the application version has changed and displays a prompt to refresh the page if necessary.
 *
 * @return {JSX.Element|null} A modal dialog prompting the user to refresh the page if the version has changed, or null if there is no change.
 */
export function VersionCheck() {
  const { hasChanged, isAuthenticated } = useVersionCheck();
  const submit = useSubmit();
  const handledRef = useRef(false);

  useEffect(() => {
    if (hasChanged && !handledRef.current) {
      handledRef.current = true;
      componentLog.info('App Version has changed');
      if (isAuthenticated) {
        const formData = new FormData();
        formData.append('redirectUrl', location.pathname + location.search);
        submit(formData, { method: 'post', action: '/infra/auth/login', navigate: false });
      } else {
        window.location.reload();
      }
    }
  }, [hasChanged, isAuthenticated, submit]);

  if (hasChanged) {
    return <div className={'fixed inset-0 z-[1000] w-full h-full opacity-90 bg-black/50'}></div>;
  }

  return null;
}
